import CloseIcon from '@mui/icons-material/Close';
import {
    Box,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid2,
    IconButton,
    Typography,
} from '@mui/material';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../../../../Hooks/ToasContext';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Input } from '../../../Form/Input/input.component';
import InputMaskComponent from '../../../Form/InputMask/input-mask.component';
import { Button } from '../../../Form/Button/button.scomponent';
import { IAddressEntity, ICreateAddressPayload } from '../../../../Services/api/addresses/addresses.interface';
import { SearchAddressesUseCase } from '../../../../Usecase/Addresses/search-addresses-usecase';
import { removeNonNumeric } from '../../../../utils/removeNonNumeric';

interface IAddressVariantProps {
    addressSelected?: IAddressEntity
    onClose: () => void
    handleNewLocalAddress: (address: IAddressEntity) => void
    handleUpdateAddress: (address: IAddressEntity) => void
    handleUpdateLocalAddress: (address: IAddressEntity) => void
    searchAddressesUseCase: SearchAddressesUseCase
    handleCreateNewAddress: (address: ICreateAddressPayload) => Promise<void>
    fromEdit?: boolean
    customerId?: string
}

interface CreateAddressSchemaValidation {
    name: string;
    number?: string;
    complement?: string;
    locality: string;
    zipcode: string;
    city: string;
    province: string
}

const CreateAddressSchemaValidation = Yup.object().shape({
    name: Yup.string().required('Rua é obrigatório'),
    number: Yup.string(),
    complement: Yup.string(),
    locality: Yup.string().required('Bairro é obrigatório'),
    zipcode: Yup.string().required('CEP é obrigatório'),
    city: Yup.string().required('Cidade é obrigatório'),
    province: Yup.string()
      .required('Estado é obrigatório')
      .max(2, 'Estado deve ter no máximo 2 caracteres')
      .transform(value => value?.toUpperCase()),
});

const AddressModal : FC<IAddressVariantProps> = ({
  addressSelected,
  onClose,
  handleNewLocalAddress,
  handleUpdateAddress,
  searchAddressesUseCase,
  fromEdit,
  handleCreateNewAddress,
  customerId,
  handleUpdateLocalAddress,
}) => {
    const { showToast } = useToast()
    const lastFetchedZipRef = useRef('')
    const [searchingZip, setSearchingZip] = useState(false)
    const isEditing = Boolean(addressSelected?.id)

    const { handleSubmit, register, control, formState, watch, setValue } = useForm<IAddressEntity>({
        resolver: yupResolver(CreateAddressSchemaValidation),
    });

    const { errors, isSubmitting } = formState
    const zipcode = watch('zipcode')
    const loading = isSubmitting || searchingZip

    const handleAction: SubmitHandler<CreateAddressSchemaValidation> = async (values) => {
        try {
          if (fromEdit) {
            if (addressSelected?.id) {
              handleUpdateAddress({ ...values, number: values.number || '', id: addressSelected.id })
            } else {
              const payloadCreate: ICreateAddressPayload = {
                ...values,
                number: values.number || '',
                customerId: customerId || ''
              }
              await handleCreateNewAddress(payloadCreate)
            }
          } else if (addressSelected?.id) {
            handleUpdateLocalAddress({ ...values, number: values.number || '', id: addressSelected.id })
          } else {
            handleNewLocalAddress({ ...values, number: values.number || '' })
          }

          onClose()
        } catch (error: any) {
            console.log("error: ", error)
            showToast(error.response?.data?.error ?? 'Erro. Por favor, tente novamente', 'error');
        }
    };

    const handleSearchAddress = useCallback(async (zip: string) => {
      if (zip.length < 8) return

      try {
        setSearchingZip(true)
        const showResult = await searchAddressesUseCase.handle(zip);

        if (showResult.isFailure) {
          showToast(String(showResult?.error?.error), 'error');
          return;
        }

        const address = showResult.getValue()

        if (address?.zipcode) {
          setValue('city', address.city)
          setValue('province', address.province)
          setValue('locality', address.locality)
          setValue('name', address.name)
          setValue('complement', address.complement || '')
        }
      } catch (error) {
        console.log("error searchAddresses function: ", error)
      } finally {
        setSearchingZip(false)
      }
    }, [searchAddressesUseCase, setValue, showToast]);

    useEffect(() => {
      if (!addressSelected) {
        lastFetchedZipRef.current = ''
        return
      }

      setValue('name', addressSelected.name)
      setValue('number', addressSelected.number)
      setValue('complement', addressSelected.complement)
      setValue('locality', addressSelected.locality)
      setValue('city', addressSelected.city)
      setValue('province', addressSelected.province)
      setValue('zipcode', addressSelected.zipcode)
      lastFetchedZipRef.current = removeNonNumeric(addressSelected.zipcode || '')
    }, [addressSelected, setValue])

    useEffect(() => {
      const normalized = removeNonNumeric(zipcode || '')

      if (normalized.length < 8) {
        if (!normalized) lastFetchedZipRef.current = ''
        return
      }

      if (lastFetchedZipRef.current === normalized) return

      lastFetchedZipRef.current = normalized
      handleSearchAddress(normalized)
    }, [zipcode, handleSearchAddress])

    return (
      <Dialog
        open
        onClose={onClose}
        aria-labelledby="address-dialog-title"
        maxWidth="lg"
        fullWidth
        scroll="paper"
        disableScrollLock={false}
        PaperProps={{
          sx: {
            maxHeight: 'min(90vh, 720px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            position: 'relative',
          },
        }}
      >
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255, 255, 255, 0.75)',
            }}
          >
            <CircularProgress size={36} />
          </Box>
        )}

        <DialogTitle
          id="address-dialog-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
            borderBottom: '1px solid',
            borderColor: 'divider',
            py: 1.5,
          }}
        >
          <Typography component="span" variant="h6" fontWeight={600}>
            {isEditing ? 'Editar Endereço' : 'Novo Endereço'}
          </Typography>
          <IconButton onClick={onClose} size="small" aria-label="Fechar">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Box
          component="form"
          onSubmit={handleSubmit(handleAction)}
          sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}
        >
          <DialogContent sx={{ flex: '1 1 auto', overflowY: 'auto', pt: 2.5 }}>
            <Grid2 container spacing={3}>
              <Grid2 size={{ xs: 12, sm: 4 }}>
                <InputMaskComponent
                  label='CEP'
                  helperText={errors?.zipcode?.message}
                  error={!!errors?.zipcode}
                  placeholder="Preencha o CEP"
                  mask='99999-999'
                  name="zipcode"
                  control={control}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 8 }}>
                <Input
                  label="Endereço"
                  placeholder="Preencha o Endereço"
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  {...register('name')}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 4 }}>
                <Input
                  label="Número"
                  placeholder="Preencha o Número"
                  error={!!errors.number}
                  helperText={errors.number?.message}
                  {...register('number')}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 4 }}>
                <Input
                  label="Complemento"
                  placeholder="Preencha o Complemento"
                  error={!!errors.complement}
                  helperText={errors.complement?.message}
                  {...register('complement')}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 4 }}>
                <Input
                  label="Bairro"
                  placeholder="Preencha o Bairro"
                  error={!!errors.locality}
                  helperText={errors.locality?.message}
                  {...register('locality')}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Input
                  label="Cidade"
                  placeholder="Preencha a Cidade"
                  error={!!errors.city}
                  helperText={errors.city?.message}
                  {...register('city')}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Input
                  label="Estado"
                  placeholder="Preencha o Estado"
                  error={!!errors.province}
                  helperText={errors.province?.message || 'Apenas Sigla. Ex: SP'}
                  {...register('province', {
                    onChange: (e) => {
                      const value = e.target.value.toUpperCase();
                      if (value.length > 2) {
                        e.target.value = value.slice(0, 2);
                      }
                      setValue('province', value.slice(0, 2));
                    }
                  })}
                />
              </Grid2>
            </Grid2>
          </DialogContent>

          <DialogActions
            sx={{
              flexShrink: 0,
              px: 3,
              py: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              gap: 1,
            }}
          >
            <Button onClick={onClose} variant='text' sx={{ minWidth: 120 }}>
              Cancelar
            </Button>
            <Button type="submit" sx={{ minWidth: 160 }} isLoading={loading}>
              Salvar
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    );
};

export default AddressModal;
